import React from 'react';
import { RequestKanbanCard } from 'src/features/requests/request-kanban/request-kanban-card';
import { Draggable, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { RequestsTableResponse, RequestStatus } from 'src/api/services/RequestClient';
import { CardProps } from '@aag-development/becreatives-ui';
import { PaginatedResponse } from 'src/api/Client';
import { DraggableCardGrip } from 'src/features/requests/draggable-card-grip';
import { cn } from 'src/lib/utils';
import { useBreakpoint } from 'src/lib/hooks';
import { requestFlow } from 'src/lib/services/api/request-api';
import { useRequestsCounts } from 'src/models';

type Props = {
  pages: PaginatedResponse<RequestsTableResponse>[];
  status: RequestStatus;
  cardProps?: (request: RequestsTableResponse) => CardProps;
  isDragDisabled?: boolean;
  pageSize: number;
  countsQueryResult?: ReturnType<typeof useRequestsCounts>;
};

// using pure component to avoid some unneeded re-renders as it is advised to do so in the docs:
// https://github.com/hello-pangea/dnd/blob/main/docs/api/droppable.md#recommended-droppable--performance-optimisation
const RequestKanbanColumnList: React.FC<Props> = React.memo(function RequestKanbanColumnList({
  pages,
  status,
  cardProps,
  isDragDisabled,
  pageSize,
  countsQueryResult,
}) {
  const { isMobile } = useBreakpoint();

  // disabling animation of item being dragged into new column/sorting
  function getStyle(style: React.CSSProperties | undefined, snapshot: DraggableStateSnapshot) {
    if (!snapshot.isDragging) return {};
    if (!snapshot.isDropAnimating) {
      return style;
    }

    return {
      ...style,
      // cannot be 0, but make it super tiny
      transitionDuration: `0.001s`,
    };
  }

  // Flatten and sort all items from all pages
  // Sort by customer activity only - most recent customer action goes to top
  const sortedItems = React.useMemo(() => {
    const allItems = pages?.flatMap((page) => page?.items || []) || [];
    
    return [...allItems].sort((a, b) => {
      // Priority sorting: Only customer actions should affect priority
      // Use reviewed_by_customer instead of updated_at to ignore editor actions
      const aCustomerReview = a.reviewed_by_customer ? new Date(a.reviewed_by_customer).getTime() : 0;
      const bCustomerReview = b.reviewed_by_customer ? new Date(b.reviewed_by_customer).getTime() : 0;
      return bCustomerReview - aCustomerReview; // Newer customer activity first (descending order)
    });
  }, [pages]);

  return (
    <>
      {sortedItems.map((request, i) => {
        const dragDisabled = isDragDisabled || request.flow === requestFlow.multiOutcome;

        return (
          <Draggable
            key={`${status}-${request.id}`}
            draggableId={request.id}
            index={i}
            isDragDisabled={dragDisabled}
          >
            {(provided, snapshot) => {
              const cardWrapperProps = isMobile ? { ...provided.dragHandleProps } : {};

              return (
                <div
                  ref={provided.innerRef}
                  className={'mx-2 mb-3'}
                  {...provided.draggableProps}
                  style={getStyle(provided.draggableProps.style, snapshot)}
                  {...cardWrapperProps}
                >
                  <RequestKanbanCard
                    request={request}
                    cardProps={cardProps}
                    countsQueryResult={countsQueryResult}
                    cardTop={
                      !isMobile && (
                        <DraggableCardGrip
                          className={cn(
                            'tw-absolute tw-left-1/2 tw-top-0 tw-hidden tw-animate-in tw-fade-in group-hover:tw-block',
                            snapshot.isDragging && 'tw-block',
                            dragDisabled && '!tw-hidden',
                          )}
                          iconProps={{
                            className: 'tw-h-4 tw-w-4',
                          }}
                          {...provided.dragHandleProps}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        />
                      )
                    }
                  />
                </div>
              );
            }}
          </Draggable>
        );
      })}
    </>
  );
});

export { RequestKanbanColumnList };
